import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Star, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicReview() {
  const { subId } = useParams();
  const [params] = useSearchParams();
  const contactId = params.get("c");
  const [cfg, setCfg] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/review/${subId}`).then(({ data }) => setCfg(data)).catch(() => setCfg(false));
  }, [subId]);

  const submit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/review/${subId}`, { rating, comment, name, contact_id: contactId });
      if (data.routed === "public" && data.google_url) {
        window.location.href = data.google_url;
        return;
      }
      setDone(data.routed);
    } catch { setDone("private"); }
    finally { setSubmitting(false); }
  };

  if (cfg === null) return <div className="min-h-screen flex items-center justify-center bg-[#0b1220]"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>;
  if (cfg === false) return <div className="min-h-screen flex items-center justify-center bg-[#0b1220] text-white/70">Review page not found.</div>;

  const color = cfg.brand?.color || "#6366F1";

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(140deg,#0b1220,#111827)" }}>
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl" data-testid="public-review-card">
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color }} />
            <h1 className="text-xl font-bold text-slate-900">Thank you for your feedback!</h1>
            <p className="text-slate-500 mt-2 text-sm">We truly appreciate you taking the time. Your input helps us improve.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-xl mx-auto flex items-center justify-center text-white font-bold text-lg" style={{ background: color }}>{cfg.brand?.name?.[0] || "M"}</div>
              <h1 className="text-xl font-bold text-slate-900 mt-4">{cfg.brand?.name}</h1>
              <p className="text-slate-500 mt-2 text-sm">{cfg.request_message}</p>
            </div>
            <div className="flex justify-center gap-2 mb-6" data-testid="star-picker">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)} onClick={() => setRating(i)} data-testid={`star-${i}`}>
                  <Star className={`w-9 h-9 transition-transform hover:scale-110 ${i <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <div className="space-y-3 animate-fade-up">
                {!name && <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400" data-testid="review-name-input" />}
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} placeholder="Tell us about your experience…" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 resize-none" data-testid="review-comment-input" />
                <button onClick={submit} disabled={submitting} className="w-full rounded-lg py-3 font-semibold text-white flex items-center justify-center gap-2" style={{ background: color }} data-testid="submit-review-button">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit review"}
                </button>
                {rating < (cfg.min_public_rating || 4) && <p className="text-xs text-slate-400 text-center">Your feedback goes privately to the business owner.</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
